import React, { useState } from "react";
import styled from "styled-components";
import { useHistory } from "react-router-dom";
import { useAmazonContext } from "../../Contexts/AmazonContext";
import api from "../api";

const DeleteContainer = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.7);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
`;

const DeleteModal = styled.div`
  background-color: ${(props) =>
    props.isDarkMode ? "#2c2c2c" : "white"};
  padding: 30px;
  border-radius: 15px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);
  max-width: 500px;
  width: 90%;
  max-height: 90vh;
  overflow-y: auto;
  text-align: center;
  position: relative;
`;

const WarningIcon = styled.div`
  font-size: 60px;
  color: #dc3545;
  margin-bottom: 20px;
`;

const Title = styled.h2`
  color: ${(props) =>
    props.isDarkMode ? "var(--font-dark)" : "var(--font-light)"};
  font-family: "Quicksand", sans-serif;
  font-size: 24px;
  margin-bottom: 15px;
  font-weight: 700;
`;

const Description = styled.p`
  color: ${(props) =>
    props.isDarkMode ? "var(--font-dark)" : "var(--font-light)"};
  font-family: "Quicksand", sans-serif;
  font-size: 16px;
  line-height: 1.5;
  margin-bottom: 25px;
`;

const WarningList = styled.ul`
  text-align: left;
  color: ${(props) =>
    props.isDarkMode ? "var(--font-dark)" : "var(--font-light)"};
  font-family: "Quicksand", sans-serif;
  font-size: 14px;
  margin-bottom: 25px;
  padding-left: 20px;

  li {
    margin-bottom: 8px;
  }
`;

const ConfirmationInput = styled.input`
  width: 100%;
  padding: 12px;
  border: 2px solid #dc3545;
  border-radius: 8px;
  font-family: "Quicksand", sans-serif;
  font-size: 16px;
  margin-bottom: 20px;
  background-color: ${(props) =>
    props.isDarkMode ? "#3c3c3c" : "white"};
  color: ${(props) =>
    props.isDarkMode ? "var(--font-dark)" : "var(--font-light)"};

  &::placeholder {
    color: #999;
  }

  &:focus {
    outline: none;
    border-color: #c82333;
    box-shadow: 0 0 0 3px rgba(220, 53, 69, 0.1);
  }
`;

const ButtonContainer = styled.div`
  display: flex;
  gap: 15px;
  justify-content: center;
  flex-wrap: wrap;
`;

const Button = styled.button`
  padding: 12px 24px;
  border: none;
  border-radius: 8px;
  font-family: "Quicksand", sans-serif;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  min-width: 120px;

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const CancelButton = styled(Button)`
  background-color: #6c757d;
  color: white;

  &:hover:not(:disabled) {
    background-color: #5a6268;
    transform: translateY(-1px);
  }
`;

const DeleteButton = styled(Button)`
  background-color: #dc3545;
  color: white;

  &:hover:not(:disabled) {
    background-color: #c82333;
    transform: translateY(-1px);
  }
`;

const LoadingSpinner = styled.div`
  display: inline-block;
  width: 20px;
  height: 20px;
  border: 3px solid #ffffff;
  border-radius: 50%;
  border-top-color: transparent;
  animation: spin 1s ease-in-out infinite;
  margin-right: 8px;

  @keyframes spin {
    to { transform: rotate(360deg); }
  }
`;

const AccountDeletion = ({ isOpen, onClose }) => {
  const [confirmationText, setConfirmationText] = useState("");
  const [isDeleting, setIsDeleting] = useState(false);
  const { isDarkMode } = useAmazonContext();
  const history = useHistory();
  const userEmail = localStorage.getItem("userEmail");

  const handleDeleteAccount = async () => {
    if (confirmationText !== "DELETE MY ACCOUNT") {
      return;
    }

    setIsDeleting(true);

    try {
      // Call the backend to delete the user account
      await api.post("/deleteUser", {
        userEmail: userEmail
      });

      // Clear all localStorage data
      localStorage.clear();

      // Navigate to a goodbye page or login page
      history.replace("/account-deleted");
    } catch (error) {
      console.error("Error deleting account:", error);
      alert("There was an error deleting your account. Please try again or contact support.");
      setIsDeleting(false);
    }
  };

  const isConfirmationValid = confirmationText === "DELETE MY ACCOUNT";

  if (!isOpen) return null;

  return (
    <DeleteContainer onClick={onClose}>
      <DeleteModal isDarkMode={isDarkMode} onClick={(e) => e.stopPropagation()}>
        <WarningIcon>⚠️</WarningIcon>
        <Title isDarkMode={isDarkMode}>Delete Your Account</Title>
        <Description isDarkMode={isDarkMode}>
          This action is <strong>permanent and cannot be undone</strong>. 
          Deleting your account will remove all of your data from our system.
        </Description>
        
        <WarningList isDarkMode={isDarkMode}>
          <li>All your course progress will be lost</li>
          <li>Your learning streak will be reset</li>
          <li>Your quiz scores and achievements will be deleted</li>
          <li>Your account cannot be recovered after deletion</li>
        </WarningList>

        <Description isDarkMode={isDarkMode}>
          To confirm deletion, please type <strong>"DELETE MY ACCOUNT"</strong> below:
        </Description>

        <ConfirmationInput
          isDarkMode={isDarkMode}
          type="text"
          placeholder="Type: DELETE MY ACCOUNT"
          value={confirmationText}
          onChange={(e) => setConfirmationText(e.target.value)}
          disabled={isDeleting}
        />

        <ButtonContainer>
          <CancelButton onClick={onClose} disabled={isDeleting}>
            Cancel
          </CancelButton>
          <DeleteButton
            onClick={handleDeleteAccount}
            disabled={!isConfirmationValid || isDeleting}
          >
            {isDeleting && <LoadingSpinner />}
            {isDeleting ? "Deleting..." : "Delete Account"}
          </DeleteButton>
        </ButtonContainer>
      </DeleteModal>
    </DeleteContainer>
  );
};

export default AccountDeletion;
